<script setup lang="ts">
    import { computed, inject, onUnmounted, ref, shallowRef, watch } from 'vue';
    import { OrthographicCamera } from 'three';
    import { stop, type StopHandle } from '@/util/stopHandle';
    import { createSceneRuntime } from '@/lib/threeScene/runtime/createSceneRuntime';
    import { useHipSpineXRayEditViewModel } from '@/views/3d-planner/hipSpine/xrayEdit/useHipSpineXRayEditViewModel';
    import { LOUPE_DIAMETER_PX } from '@/views/3d-planner/hipSpine/loupe/loupeCamera';
    import {
        createLoupeFrameUpdater,
        LOUPE_SOURCE_KEY,
        type LoupeSource,
    } from '@/views/3d-planner/hipSpine/loupe/loupeView';

    // Gap (px) between the cursor and the right edge of the loupe circle.
    const CURSOR_GAP_PX = 12;

    const editVm = useHipSpineXRayEditViewModel();
    const source = inject(LOUPE_SOURCE_KEY, shallowRef<LoupeSource | null>(null));

    const containerRef = ref<HTMLDivElement>();
    const canvasRef = ref<HTMLCanvasElement>();
    const cursor = ref({ x: 0, y: 0 });

    const onPointerMove = (event: PointerEvent) => {
        cursor.value = { x: event.clientX, y: event.clientY };
    };

    // Offset the circle to the LEFT of the cursor so it never sits under it.
    const overlayStyle = computed(() => ({
        left: `${cursor.value.x - CURSOR_GAP_PX - LOUPE_DIAMETER_PX}px`,
        top: `${cursor.value.y - LOUPE_DIAMETER_PX / 2}px`,
        width: `${LOUPE_DIAMETER_PX}px`,
        height: `${LOUPE_DIAMETER_PX}px`,
    }));

    // Hand-rolled runtime lifecycle (NOT useScene) because this component renders a
    // FOREIGN scene it does not own — the same documented exception as OverlayMainView.vue.
    //
    // The runtime is built once on the first drag and persists for the modal's lifetime
    // (disposed in onUnmounted). Tearing it down and rebuilding per drag would recompile
    // the shared scene's shader programs on every drag-start, causing a visible hitch when
    // the magnifier appears. Persisting keeps appearance instant; the idle cost is a tiny
    // 100×100 render of a simple 2D scene.
    function buildRuntime(src: LoupeSource): StopHandle | null {
        const container = containerRef.value;
        const canvas = canvasRef.value;
        if (!container || !canvas) return null;
        const loupeCamera = new OrthographicCamera();
        const runtime = createSceneRuntime({ container, canvas, scene: src.scene, camera: loupeCamera });
        runtime.beforeRender(
            createLoupeFrameUpdater(loupeCamera, {
                mainCamera: src.mainCamera,
                getMainCanvasHeightPx: () => src.mainCanvas.clientHeight,
                getTarget: () => editVm.loupeTargetWorld(),
            }),
        );
        return runtime.stop;
    }

    function setCursorTracking(active: boolean): void {
        if (active) {
            window.addEventListener('pointermove', onPointerMove);
        } else {
            window.removeEventListener('pointermove', onPointerMove);
        }
    }

    let teardown: StopHandle | null = null;
    watch(
        () => [source.value, editVm.loupe.active] as const,
        ([src, active]) => {
            if (src && active && !teardown) {
                const handle = buildRuntime(src);
                if (handle) teardown = handle;
            }
            setCursorTracking(active);
        },
        { immediate: true, flush: 'post' },
    );

    onUnmounted(() => {
        window.removeEventListener('pointermove', onPointerMove);
        stop(teardown);
        teardown = null;
    });
</script>

<template>
    <Teleport to="body">
        <div
            v-show="editVm.loupe.active"
            ref="containerRef"
            class="hip-spine-loupe"
            :style="overlayStyle"
            data-testid="hip-spine-loupe"
        >
            <canvas ref="canvasRef" class="hip-spine-loupe-canvas" />
        </div>
    </Teleport>
</template>

<style scoped lang="scss">
    .hip-spine-loupe {
        position: fixed;
        z-index: 2400;
        border: 2px solid rgb(var(--v-theme-outline));
        border-radius: 50%;
        overflow: hidden;
        pointer-events: none;
        background: #262625;
    }

    .hip-spine-loupe-canvas {
        width: 100%;
        height: 100%;
        display: block;
    }
</style>
